<script setup>
// Public content page for the professional-manager search intent ("VvE software voor
// beheerders", "VvE bijdragen beheren"). Body copy is authored in Dutch in this page's
// components rather than in i18n, for the same reason the legal pages do it — see
// Privacybeleid.vue. Only the tab title and meta description live in nl.js.
//
// The page is deliberately HYBRID, and the seam is meant to be visible.
//
// The first three beats are cinematic full-screen panels in the homepage's grammar,
// because a manager arriving here has to be sold; everything from the seam down is a
// plain scrolling document, because it is a spec sheet they come BACK to in order to
// compare. Forcing the boundary list and the pricing detail into one-screen-per-beat
// would damage the half of the page that does the most convincing.
//
// That is why this route mounts `snap-pages-loose` ALONGSIDE `snap-pages` for its
// whole lifetime — `scroll-snap-type: y proximity`, never `mandatory`. Under
// mandatory, a long region with no snap point of its own gets dragged to the nearest
// one (application.css documents that wedge from the homepage), and the reference half
// is exactly such a region: it would be unusable. Proximity still pulls the three
// panels square when a gesture ends near them. Both classes go on together in the same
// onMounted — the reference half depends on the relaxation from the very first frame,
// so it can never be toggled mid-scroll the way Landing.vue does it.
//
// For the same reason, ONLY the three cinematic sections carry `data-page-label`:
// `html.snap-pages [data-page-label]:not(.snap-page)` grants a snap point, so a
// reference section with that attribute would silently become one.
import { ref, onMounted, onUnmounted } from "vue";
import LandingNav from "@/components/landing/LandingNav.vue";
import LandingFooter from "@/components/landing/LandingFooter.vue";
import BeheerdersHero from "@/components/beheerders/BeheerdersHero.vue";
import BeheerdersExceptions from "@/components/beheerders/BeheerdersExceptions.vue";
import BeheerdersArrangement from "@/components/beheerders/BeheerdersArrangement.vue";
import BeheerdersDetail from "@/components/beheerders/BeheerdersDetail.vue";
import BeheerdersTrust from "@/components/beheerders/BeheerdersTrust.vue";
import BeheerdersBoundary from "@/components/beheerders/BeheerdersBoundary.vue";
import BeheerdersPricing from "@/components/beheerders/BeheerdersPricing.vue";
import BeheerdersCta from "@/components/beheerders/BeheerdersCta.vue";
import { anchorScroll, scrollElementIntoView, resolveHash } from "@/lib/scroll";

// The header's own table of contents, and the one item that leaves it. The shared nav's
// away link points at THIS page from the homepage, so here it has to point back: the
// brand mark is a link too, but a logo is a convention, not a signpost, and someone who
// arrived on this page from a search result has never seen the homepage.
const anchors = [
  { href: "#inrichting", label: "De inrichting" },
  { href: "#wat-je-ziet", label: "Wat je ziet" },
  { href: "#grens", label: "De grens" },
  { href: "#prijs", label: "Prijs" },
];

// The seam's index: real wayfinding for the half of the page a manager returns to.
const reference = [
  { href: "#wat-je-ziet", label: "Wat je per VvE ziet" },
  { href: "#geld", label: "Wat bij de VvE blijft" },
  { href: "#grens", label: "Wat Pandig niet doet" },
  { href: "#prijs", label: "Wat het kost" },
  { href: "#aan-de-slag", label: "Aan de slag" },
];

const root = ref(null);
const referenceHalf = ref(null);

// The right-edge pager, three dots for three cinematic panels — and it FADES OUT below
// the seam. It belongs to the cinematic mode; the mode ending is why it goes. That
// announces the hybrid rather than papering over it, and it avoids a three-dot pager
// claiming this page is three screens long.
const pages = ref([]);
const pageLabels = ref([]);
const activePage = ref(0);
const hoveredPage = ref(null);
const inReference = ref(false);
let observer = null;
let seamObserver = null;

function gotoPage(i, event) {
  const el = pages.value[i];
  if (!el) return;

  scrollElementIntoView(el);
  // A dot clicked with a mouse keeps focus afterwards, and a focused button owns the
  // space bar, so the next space press would do nothing at all. Keyboard activation
  // (detail 0) keeps its focus, as it must.
  if (event?.detail) event.currentTarget.blur();
}

onMounted(() => {
  document.documentElement.classList.add("snap-pages", "snap-pages-loose");

  pages.value = [...root.value.querySelectorAll("[data-page-label]")];
  pageLabels.value = pages.value.map((page) => page.dataset.pageLabel ?? "");
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const idx = pages.value.indexOf(entry.target);
        if (idx !== -1) activePage.value = idx;
      });
    },
    // A band around the centre line, not an exact zero-height line — a single line is
    // one frame away from missing a transition entirely.
    { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
  );
  pages.value.forEach((page) => observer.observe(page));

  // "Has the seam passed the middle of the screen yet?", asked of the whole reference
  // half rather than of the seam alone. The root is the TOP half of the viewport, so
  // the answer is a plain isIntersecting: the half below the seam reaches into the top
  // half of the screen exactly when the seam has travelled past the middle, and it
  // keeps reaching it all the way down to the footer.
  //
  // Both details are load-bearing. Watching the seam itself needed a threshold on the
  // seam's own position, and a band at 50% means the crossing happens at
  // `top < innerHeight / 2`, not at `top < 0` — measured against the viewport top the
  // answer arrives after the observer has stopped firing, so it never arrives at all.
  // And a target the size of the seam changes state twice on the way past, so an
  // instant jump (a deep link landing on #prijs) skips both edges and reports nothing.
  // A target that spans the region has one edge and cannot be jumped over.
  seamObserver = new IntersectionObserver(([entry]) => (inReference.value = entry.isIntersecting), {
    rootMargin: "0px 0px -50% 0px",
    threshold: 0,
  });
  seamObserver.observe(referenceHalf.value);

  // A shared /voor-vve-beheerders#prijs link has to land on the price. The browser's
  // own attempt happened while #app was still the pre-rendered shell.
  resolveHash();
});

onUnmounted(() => {
  observer?.disconnect();
  seamObserver?.disconnect();
  document.documentElement.classList.remove("snap-pages", "snap-pages-loose");
});
</script>

<template>
  <div ref="root" class="flex min-h-dvh flex-col">
    <LandingNav :anchors="anchors" :away-link="{ to: '/', label: 'Pandig voor VvE\'s' }" />

    <nav
      class="fixed right-4 top-1/2 z-30 hidden -translate-y-1/2 flex-col gap-3 transition-opacity duration-300 lg:flex"
      :class="inReference ? 'pointer-events-none opacity-0' : 'opacity-100'"
      :aria-hidden="inReference"
      aria-label="Pagina's"
    >
      <button
        v-for="(page, i) in pages"
        :key="i"
        type="button"
        class="group relative size-2.5 border-2 border-foreground transition-all duration-200 hover:scale-125"
        :class="activePage === i ? 'scale-125 bg-foreground' : 'bg-transparent'"
        :aria-label="`Ga naar ${pageLabels[i]}`"
        :aria-current="activePage === i"
        :tabindex="inReference ? -1 : 0"
        @click="gotoPage(i, $event)"
        @pointerenter="hoveredPage = i"
        @pointerleave="hoveredPage = null"
        @focus="hoveredPage = i"
        @blur="hoveredPage = null"
      >
        <span
          class="pointer-events-none absolute right-full top-1/2 mr-3 -translate-y-1/2 whitespace-nowrap border-2 border-foreground bg-card px-2 py-0.5 font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-foreground shadow-[var(--shadow-hard-sm)] transition-opacity duration-200"
          :class="hoveredPage === i || activePage === i ? 'opacity-100' : 'opacity-0'"
        >
          {{ pageLabels[i] }}
        </span>
      </button>
    </nav>

    <main class="flex-1">
      <!-- 1. The promise, and the instrument that proves it. -->
      <BeheerdersHero />
      <!-- 2. The month a manager recognises, before Pandig describes itself. -->
      <BeheerdersExceptions />
      <!-- 3. How it is arranged: one account, a separate money flow per VvE. -->
      <BeheerdersArrangement />

      <!-- Everything from the seam down, in one element: the pager watches this to know
           the cinematic half is over. See the observer in onMounted. -->
      <div ref="referenceHalf">
        <!-- The seam. The pitch stops here and the reference begins, and saying so out
             loud is better than letting the reader discover it by the scroll changing
             character under their hands. -->
        <div class="border-t-2 border-foreground bg-foreground text-background">
          <div class="mx-auto flex w-full max-w-6xl flex-col gap-2 px-5 py-4 sm:flex-row sm:items-baseline sm:gap-6 sm:py-5">
            <p class="shrink-0 font-mono text-[0.625rem] font-semibold uppercase tracking-[0.14em] text-background/60 sm:text-[0.6875rem]">Referentie</p>
            <nav class="flex flex-wrap items-baseline gap-x-4 gap-y-1" aria-label="Referentie">
              <a
                v-for="item in reference"
                :key="item.href"
                :href="item.href"
                class="font-mono text-[0.625rem] font-semibold uppercase tracking-[0.08em] text-background underline-offset-4 transition-opacity hover:opacity-70 hover:underline sm:text-[0.6875rem]"
                @click="anchorScroll"
              >
                {{ item.label }}
              </a>
            </nav>
          </div>
        </div>

        <!-- 4-8. Read to be used, so they scroll plainly and carry no snap point. -->
        <BeheerdersDetail />
        <BeheerdersTrust />
        <BeheerdersBoundary />
        <BeheerdersPricing />
        <BeheerdersCta />
      </div>
    </main>

    <LandingFooter />
  </div>
</template>
